import { useQuery } from "@apollo/client";
import { useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import getYear from "date-fns/getYear";
import { useIntl } from "react-intl";
import { CalendarDaysIcon, HandCoinsIcon, HandshakeIcon, TrendingDownIcon } from "lucide-react";

import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import classNames from "components/ui/classNames";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs/v2/TabsV2";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";

import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";
import BrazilianIssuerDividendsQuery from "lib/queries/BrazilianIssuerDividendsQuery";
import type {
  IBrazilianIssuerDividendsQuery,
  IBrazilianIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/BrazilianIssuerDividendsQuery.generated";
import type {
  IDividendsPaidByYearDetails,
  IDividendsPaidDetails,
  IStockDividendsCorporateAction,
} from "types/graphqlTypes";
import { ISecurityCountry } from "types/graphqlTypes";
import parseDateYmd from "lib/utils/parseDateYmd";

import DividendsPaidTabContent from "../content/DividendsPaidTabContent";
import MandatoryMinimumDividendTabContent from "../content/MandatoryMinimumDividendTabContent";
import BrazilianStockDividendsPaidTabContet from "../content/BrazilianStockDividendsPaidTabContet";
import YearlyDividendsPaidTabContent from "../content/YearlyDividendsPaidTabContent";
import ContentSectionBreadcrumbs from "../ContentSectionBreadcrumbs";
import DividendsPaidSummary from "../summary/DividendsPaidSummary";

import FilterButtonTab from "./utils/FilterButtonTab";

enum DividendsTab {
  Dividends = "DIVIDENDS",
  StockDividends = "STOCK_DIVIDENDS",
  YearlyDividends = "YEARLY_DIVIDENDS",
  MandatoryMinimumDividend = "MANDATORY_MINIMUM_DIVIDEND",
}

interface IBrazilianDividendsCollapsibleProps {
  tickerSymbol: string;
  isExpanded: boolean;
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
  onToggleCollapsed: () => void;
}

export default function BrazilianDividendsCollapsible({
  tickerSymbol,
  issuer,
  isExpanded,
  onToggleCollapsed,
}: IBrazilianDividendsCollapsibleProps) {
  const intl = useIntl();
  const [selectedTab, setSelectedTab] = useState<DividendsTab>(DividendsTab.Dividends);
  const [filterByTickerSymbol, setFilterByTickerSymbol] = useState<string>(issuer.securities[0].tickerSymbol);
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);
  const [filterStockDividendsByYear, setFilterStockDividendsByYear] = useState<number | null>(null);

  const { data, previousData, loading } = useQuery<
    IBrazilianIssuerDividendsQuery,
    IBrazilianIssuerDividendsQueryVariables
  >(BrazilianIssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: ISecurityCountry.Br,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  const dividendsPaid = data && data.dividendsPaid ? data.dividendsPaid : previousData?.dividendsPaid;
  const stockDividendsPaid =
    data && data.stockDividendsPaid ? data.stockDividendsPaid : previousData?.stockDividendsPaid;

  if (!dividendsPaid) {
    return null;
  }

  const { availableYears, dividendYieldByTickerSymbol, dividendsPaidByYear, nodes } = dividendsPaid;

  const dividendsTabs = [
    {
      value: DividendsTab.Dividends,
      title: intl.formatMessage({
        defaultMessage: "Proventos",
        id: "InBalx",
      }),
      icon: <HandCoinsIcon size={16} strokeWidth={2} />,
      content: (
        <DividendsPaidTabContent
          dividendsPaid={nodes?.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node)) ?? []}
          availableYears={availableYears ?? []}
          loading={loading}
          filterDividendsByYear={filterDividendsByYear}
          setFilterDividendsByYear={(year) => setFilterDividendsByYear(year)}
        />
      ),
    },
    {
      value: DividendsTab.StockDividends,
      title: intl.formatMessage({
        defaultMessage: "Bonificação",
        id: "UjC0PD",
      }),
      icon: <HandshakeIcon size={16} strokeWidth={2} />,
      content: (
        <BrazilianStockDividendsPaidTabContet
          data={stockDividendsPaid?.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node)) ?? []}
          loading={loading}
          filterStockDividendsByYear={filterStockDividendsByYear}
          setFilterStockDividendsByYear={(year) => setFilterStockDividendsByYear(year)}
        />
      ),
      isHidden: !stockDividendsPaid || (stockDividendsPaid && stockDividendsPaid.length === 0),
    },
    {
      value: DividendsTab.YearlyDividends,
      title: intl.formatMessage({
        defaultMessage: "Proventos Anualizados",
        id: "O02jtq",
      }),
      icon: <CalendarDaysIcon size={16} strokeWidth={2} />,
      content: (
        <YearlyDividendsPaidTabContent
          dividendsPaidByYear={
            dividendsPaidByYear?.filter((node) => filterActionsByTickerSymbol(filterByTickerSymbol, node)) ?? []
          }
          loading={loading}
        />
      ),
    },
    {
      value: DividendsTab.MandatoryMinimumDividend,
      title: intl.formatMessage({
        defaultMessage: "Mín. Obrigatório",
        description: "Dividendo Mínimo Obrigatório",
        id: "YAFeST",
      }),
      icon: <TrendingDownIcon size={16} strokeWidth={2} />,
      content: (
        <MandatoryMinimumDividendTabContent
          securities={issuer.securities}
          mandatoryMinimumDividend={issuer.mandatoryMinimumDividend}
        />
      ),
      isHidden: issuer.mandatoryMinimumDividend === null,
    },
  ];

  if (!data && !previousData) {
    return null;
  }

  if (!data && !previousData && loading) {
    return (
      <div className="flex w-full items-center justify-center">
        <ClipLoader size={28} color={palette.royalBlue} loading={true} />
      </div>
    );
  }

  return (
    <CollapsibleContentSection
      title="Dividendos"
      isExpanded={isExpanded}
      isPremiumSection={true}
      onToggleCollapsed={onToggleCollapsed}
      primaryColor={palette.dixie}
      renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Informações Financeiras", "Proventos"]} />}
      renderContentSummary={() => (
        <DividendsPaidSummary
          dividendsPaid={nodes ?? []}
          dividendsPaidByYear={dividendsPaidByYear ?? []}
          dividendYieldSnapshotEachSymbol={[...(dividendYieldByTickerSymbol ?? [])].sort((a, b) => {
            if (a.tickerSymbol === tickerSymbol) {
              return -1;
            }
            if (b.tickerSymbol === tickerSymbol) {
              return 1;
            }
            return b.dividendYield - a.dividendYield;
          })}
        />
      )}
      renderContent={() => (
        <div
          className={classNames(
            "relative rounded-xl border bg-white",
            "ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
            "dark:ring-1 dark:ring-slate-950 dark:ring-offset-2 dark:ring-offset-slate-900",
            "dark:border-transparent dark:bg-slate-900",
          )}
        >
          <Tabs
            className="w-full space-y-0 rounded-xl font-normal"
            defaultValue={DividendsTab.Dividends}
            value={selectedTab}
            onValueChange={(tab) => setSelectedTab(tab as DividendsTab)}
          >
            <TabsList
              className={classNames(
                "hidden w-full flex-1 divide-x overflow-hidden rounded-b-none rounded-t-xl p-0 md:flex",
                "bg-slate-50",
                "dark:divide-slate-950 dark:border-slate-900 dark:bg-slate-800",
              )}
            >
              {dividendsTabs
                .filter((tab) => !tab.isHidden)
                .map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className={classNames(
                      "w-full justify-center overflow-hidden rounded-none border-b border-b-transparent px-2 md:justify-start",
                      "border-b-slate-200 dark:border-b-slate-950",
                      "group",
                    )}
                  >
                    <div className="flex flex-col items-center gap-2 truncate py-1 text-left md:flex-row lg:px-1.5">
                      <div
                        className={classNames(
                          "flex items-center justify-center rounded-full p-2 squircle",
                          "bg-slate-50 dark:bg-indigo-300/10 dark:text-indigo-200",
                          "group-data-[state=active]:dark:bg-indigo-300/20 group-data-[state=active]:dark:text-indigo-100",
                          "group-data-[state=active]:bg-indigo-50/50 group-data-[state=active]:text-indigo-600",
                        )}
                      >
                        {tab.icon}
                      </div>
                      <span
                        className={classNames(
                          "text-xs leading-6 lg:text-base",
                          "text-slate-500 group-data-[state=active]:font-bold",
                          "group-data-[state=active]:text-slate-600 group-data-[state=active]:dark:text-slate-200",
                        )}
                      >
                        {tab.title}
                      </span>
                    </div>
                  </TabsTrigger>
                ))}
            </TabsList>

            <div className="p-3 md:hidden">
              <Select value={selectedTab} onValueChange={(value) => setSelectedTab(value as DividendsTab)}>
                <SelectTrigger
                  className={classNames(
                    "font-aeonik",
                    "w-full rounded-lg",
                    "border border-slate-200 bg-white",
                    "text-sm font-medium tracking-wide text-slate-600",
                    "disabled:select-none",
                    "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
                    "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                    "truncate",
                  )}
                >
                  <SelectValue
                    placeholder={intl.formatMessage({
                      defaultMessage: "Selecione a aba",
                      id: "CbPysw",
                    })}
                  />
                </SelectTrigger>

                <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
                  {dividendsTabs.map((tab) => (
                    <SelectItem key={tab.value} value={tab.value} className="w-full">
                      {tab.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTab !== DividendsTab.MandatoryMinimumDividend && issuer.securities.length > 1 && (
              <div className="mb-2 grid w-full grid-cols-1 gap-x-3 sm:grid-cols-2">
                {issuer.securities.map(({ tickerSymbol }) => (
                  <FilterButtonTab
                    key={`FilterTab/${tickerSymbol}`}
                    labelText={tickerSymbol}
                    totalCount={getTotalCountByTypeTab(
                      selectedTab,
                      tickerSymbol,
                      filterStockDividendsByYear,
                      nodes,
                      stockDividendsPaid || [],
                      dividendsPaidByYear,
                    )}
                    isSelected={filterByTickerSymbol === tickerSymbol}
                    onClick={() => {
                      setFilterByTickerSymbol(tickerSymbol);
                    }}
                  />
                ))}
              </div>
            )}

            {dividendsTabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value} className="border-none p-4 lg:px-6">
                {tab.content}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    />
  );
}

function filterActionsByTickerSymbol(
  tickerSymbol: string | null,
  node: IDividendsPaidDetails | IDividendsPaidByYearDetails | IStockDividendsCorporateAction,
) {
  if (tickerSymbol && tickerSymbol === node.tickerSymbol) {
    return true;
  }

  if (!tickerSymbol) {
    return true;
  }

  return false;
}

function getTotalCountByTypeTab(
  selectedTab: DividendsTab,
  tickerSymbol: string | null,
  filterStockDividendsByYear: number | null,
  nodes: IDividendsPaidDetails[],
  stockDividendsPaid: IStockDividendsCorporateAction[],
  dividendsPaidByYear: IDividendsPaidByYearDetails[],
) {
  if (selectedTab === DividendsTab.Dividends) {
    return nodes?.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
  }

  if (selectedTab === DividendsTab.StockDividends) {
    return (
      stockDividendsPaid?.filter((node) => {
        if (filterActionsByTickerSymbol(tickerSymbol, node)) {
          if (!filterStockDividendsByYear) {
            return true;
          }

          if (getYear(parseDateYmd(node.excludingDate)) === filterStockDividendsByYear) {
            return true;
          }

          return false;
        }

        return false;
      }).length ?? 0
    );
  }

  return dividendsPaidByYear?.filter((node) => filterActionsByTickerSymbol(tickerSymbol, node)).length ?? 0;
}
